import { ChevronDown, ChevronUp, Maximize2, Users } from 'lucide-react'
import {
  CarouselLayout,
  ParticipantTile,
  useEnsureTrackRef,
  useFocusToggle,
} from '@livekit/components-react'
import type { TrackReferenceOrPlaceholder } from '@livekit/components-react'
import { cn } from '@/custolink-newdesign/lib/utils'

const FOCUS_BUTTON_CLASS =
  'rs-thumb-focus-btn cl:absolute cl:top-1.5 cl:right-1.5 cl:z-10 cl:flex cl:min-h-[44px] cl:min-w-[44px] cl:pointer-fine:min-h-8 cl:pointer-fine:min-w-8 cl:items-center cl:justify-center cl:gap-1 cl:rounded-full cl:bg-black/60 cl:px-2.5 cl:text-[11px] cl:font-medium cl:text-white cl:hover:bg-black/80 cl:focus-visible:outline-2 cl:focus-visible:outline-white'

/**
 * One tile inside the CarouselLayout. Reads its track from the TrackRefContext that
 * CarouselLayout provides per track (useEnsureTrackRef), rather than taking it as a prop.
 */
function ThumbnailTile(p: { compact: boolean }) {
  const trackRef = useEnsureTrackRef()
  const name = trackRef.participant.name || trackRef.participant.identity

  // useFocusToggle's own base props carry the lk-focus-toggle-button class, which we hide
  // globally (it is LiveKit's hover-only toggle, unreachable on touch); our className is
  // re-applied after the spread so this labelled button stays visible and touchable.
  const { mergedProps } = useFocusToggle({
    trackRef,
    props: {
      className: FOCUS_BUTTON_CLASS,
      'aria-label': `Show ${name} on stage`,
    },
  })

  return (
    <div className="cl:relative cl:h-full cl:w-full">
      <ParticipantTile />
      <button type="button" {...mergedProps} className={cn(FOCUS_BUTTON_CLASS)}>
        <Maximize2 className="cl:size-3.5" />
        {/* Narrow picture-in-picture tiles keep the icon only; the aria-label still names it. */}
        {!p.compact && <span>Show on stage</span>}
      </button>
    </div>
  )
}

/**
 * Collapsible strip of non-stage tracks (D-04). Always shows a count, even when collapsed, and
 * every tile carries its own "Show on stage" button (useFocusToggle) so pinning the stage never
 * depends on hover or on the thumbnails being expanded.
 */
export function ParticipantThumbnails(p: {
  tracks: TrackReferenceOrPlaceholder[]
  collapsed: boolean
  onToggleCollapsed: () => void
  orientation: 'vertical' | 'horizontal'
  /** Narrow picture-in-picture strip on a phone. */
  compact?: boolean
}) {
  const countLabel = `Participants (${p.tracks.length})`

  return (
    <div className="rs-thumbs cl:flex cl:flex-col cl:bg-neutral-950/80">
      <button
        type="button"
        aria-expanded={!p.collapsed}
        aria-label={countLabel}
        className="cl:flex cl:min-h-[44px] cl:w-full cl:items-center cl:gap-1.5 cl:px-2 cl:text-xs cl:font-medium cl:text-white"
        onClick={p.onToggleCollapsed}
      >
        <Users className="cl:size-3.5 cl:shrink-0" />
        <span className="cl:min-w-0 cl:truncate">
          {p.compact === true ? `(${p.tracks.length})` : countLabel}
        </span>
        {p.collapsed ? (
          <ChevronDown className="cl:size-3.5 cl:shrink-0" />
        ) : (
          <ChevronUp className="cl:size-3.5 cl:shrink-0" />
        )}
      </button>

      {!p.collapsed && (
        <div className="cl:min-h-0 cl:flex-1 cl:overflow-auto">
          <CarouselLayout tracks={p.tracks} orientation={p.orientation}>
            <ThumbnailTile compact={p.compact === true} />
          </CarouselLayout>
        </div>
      )}
    </div>
  )
}
